import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { parseHash } from '../utils/hashRouting';
import type { ModalType } from '../utils/hashRouting';
import { useUIStore } from '../stores/ui/uiStore';
import { useDataStore } from '../stores/data/dataStore';

/**
 * 해시 라우팅 핸들러
 * URL 해시를 파싱하여 모달을 자동으로 엽니다.
 */
const HashRouteHandler: React.FC = () => {
  const location = useLocation();
  const {
    setSelectedToken,
    openTokenDetailModal,
    openSocialDetailModal,
    openNoteDetailModal,
    isTokenDetailModalOpen,
    isSocialDetailModalOpen,
    isNoteDetailModalOpen,
    selectedToken,
  } = useUIStore();
  const { getTokenData, isInitialized } = useDataStore();
  const hasProcessedHash = useRef<string | null>(null);

  const openModalForType = useCallback(
    (modalType: ModalType, tokenAddress: string, tokenData: ReturnType<typeof getTokenData>) => {
      if (!tokenData) return;

      setSelectedToken({
        tokenAddress,
        symbol: tokenData.tokenInfo.tokenSymbol || 'Unknown',
      });

      switch (modalType) {
        case 'token':
          openTokenDetailModal();
          break;
        case 'social':
          openSocialDetailModal();
          break;
        case 'note':
          openNoteDetailModal();
          break;
      }
    },
    [setSelectedToken, openTokenDetailModal, openSocialDetailModal, openNoteDetailModal],
  );

  useEffect(() => {
    // 데이터가 초기화되지 않았으면 대기
    if (!isInitialized) {
      return;
    }


    // 해시 파싱
    const hashData = parseHash();
    const currentHash = location.hash;

    // 해시가 없으면 처리하지 않음
    if (!hashData) {
      hasProcessedHash.current = null;
      return;
    }

    // 같은 해시를 이미 처리했으면 중복 처리 방지
    if (hasProcessedHash.current === currentHash) {
      return;
    }

    const { tokenAddress, modalType } = hashData;

    const modalOpenState: Record<ModalType, boolean> = {
      token: isTokenDetailModalOpen,
      social: isSocialDetailModalOpen,
      note: isNoteDetailModalOpen,
    };

    // 이미 올바른 모달이 열려있는지 확인
    if (modalOpenState[modalType] && selectedToken?.tokenAddress === tokenAddress) {
      hasProcessedHash.current = currentHash;
      return;
    }

    // 토큰 데이터 가져오기
    const tokenData = getTokenData(tokenAddress);

    if (!tokenData) {
      // 토큰 데이터가 없으면 모달을 열 수 없음
      console.warn(`Token data not found for address: ${tokenAddress}`);
      return;
    }

    openModalForType(modalType, tokenAddress, tokenData);
    hasProcessedHash.current = currentHash;
  }, [
    location.hash,
    isInitialized,
    getTokenData,
    setSelectedToken,
    openTokenDetailModal,
    openSocialDetailModal,
    openNoteDetailModal,
    isTokenDetailModalOpen,
    isSocialDetailModalOpen,
    isNoteDetailModalOpen,
    selectedToken?.tokenAddress,
    openModalForType,
  ]);

  return null;
};

export default HashRouteHandler;

